import {observer} from "mobx-react-lite";
import {useCallback, useContext, useEffect, useRef, useState} from "react";
import styled, {useTheme} from "styled-components";
import {Theme, StyledProps, CvvColors} from "../../theme";
import {uploader} from "../../proto/compiled";
import {FileUploadStore} from "../../stores/file_upload_store";
import {ApiContext} from "../../index";
import {MdClose, MdUploadFile, MdInsertDriveFile} from "react-icons/md";
import {toast} from "react-toastify";

const UploadContainer = styled.div<{isDragActive?: boolean, hasError?: boolean}>`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 16px;
  border: 2px dashed ${({theme, isDragActive, hasError}: StyledProps & {hasError?: boolean}) =>
    hasError ? CvvColors.error : isDragActive ? theme.content50 : theme.content30};
  border-radius: 8px;
  background-color: ${({theme, isDragActive}: StyledProps) => isDragActive ? theme.content5 : 'transparent'};
  cursor: pointer;
  transition: all 200ms ease;
  min-height: 80px;

  &:hover {
    border-color: ${({theme}: StyledProps) => theme.content50};
    background-color: ${({theme}: StyledProps) => theme.content5};
  }
`;

const ErrorText = styled.span`
  margin-top: 8px;
  color: ${CvvColors.error};
  font-size: 14px;
`;

const FileDisplay = styled.div`
  display: flex;
  align-items: center;
  padding: 8px 12px;
  border: 1px solid ${({theme}: StyledProps) => theme.content20};
  border-radius: 8px;
  background-color: ${({theme}: StyledProps) => theme.content5};
`;

const UploadWrapper = styled.div`
  padding: 4px;
`;

const CompactUploadButton = styled.div`
  display: flex;
  align-items: center;
  padding: 4px 8px;
  border: 1px solid ${({theme}: StyledProps) => theme.content30};
  border-radius: 4px;
  cursor: pointer;
  color: ${({theme}: StyledProps) => theme.content50};
  font-size: 13px;
  gap: 4px;

  &:hover {
    border-color: ${({theme}: StyledProps) => theme.content50};
    background-color: ${({theme}: StyledProps) => theme.content5};
  }
`;

const FileName = styled.a`
  flex: 1;
  margin-left: 8px;
  color: ${({theme}: StyledProps) => theme.content};
  text-decoration: none;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  &:hover {
    text-decoration: underline;
  }
`;

const FileNameText = styled.span`
  flex: 1;
  margin-left: 8px;
  color: ${({theme}: StyledProps) => theme.content};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const DeleteButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({theme}: StyledProps) => theme.content50};

  &:hover {
    color: ${CvvColors.error};
  }
`;

const ProgressBar = styled.div<{progress: number}>`
  width: 100%;
  height: 4px;
  background-color: ${({theme}: StyledProps) => theme.content20};
  border-radius: 2px;
  margin-top: 8px;
  overflow: hidden;

  &::after {
    content: '';
    display: block;
    height: 100%;
    width: ${({progress}) => progress * 100}%;
    background-color: ${({theme}: StyledProps) => theme.content50};
    transition: width 200ms ease;
  }
`;

const UploadText = styled.span`
  margin-top: 8px;
  color: ${({theme}: StyledProps) => theme.content50};
  font-size: 14px;
`;

interface FileUploadInputProps {
  file?: uploader.IUFile;
  onFileChanged?: (file?: uploader.IUFile) => void;
  extensions?: string[];
  label?: string;
  compact?: boolean;
  multiple?: boolean;
  onFilesChanged?: (files: uploader.IUFile[]) => void;
  maxFiles?: number;
  excludeNames?: Set<string>;
}

interface MultiFileUploadInputProps {
  files?: uploader.IUFile[];
  onFilesChanged: (files: uploader.IUFile[]) => void;
  extensions?: string[];
  label?: string;
  compact?: boolean;
  maxFiles?: number;
}

const MultiUploadList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

export const MultiFileUploadInput = observer((props: MultiFileUploadInputProps) => {
  const files = props.files ?? [];
  const remaining = props.maxFiles != null
    ? Math.max(0, props.maxFiles - files.length)
    : undefined;
  const existingNames = new Set(
    files.map(f => f.name).filter((n): n is string => !!n)
  );
  return (
    <MultiUploadList>
      {files.map((file, idx) => (
        <FileUploadInput
          key={file.id ?? `idx-${idx}`}
          file={file}
          extensions={props.extensions}
          compact={props.compact}
          onFileChanged={(f) => {
            const next = files.slice();
            if (f) {
              next[idx] = f;
            } else {
              next.splice(idx, 1);
            }
            props.onFilesChanged(next);
          }}
        />
      ))}
      {(remaining === undefined || remaining > 0) && (
        <FileUploadInput
          key={`add-slot-${files.length}`}
          extensions={props.extensions}
          compact={props.compact}
          label={props.label}
          multiple
          maxFiles={remaining}
          excludeNames={existingNames}
          onFilesChanged={(newFiles) => props.onFilesChanged([...files, ...newFiles])}
        />
      )}
    </MultiUploadList>
  );
});

export const FileUploadInput = observer((props: FileUploadInputProps) => {
  const api = useContext(ApiContext);
  const theme = useTheme() as Theme;
  const inputRef = useRef<HTMLInputElement>(null);
  const [uploadStore] = useState(() => new FileUploadStore(
    api,
    props.extensions ? new Set(props.extensions) : undefined
  ));
  const [isDragActive, setIsDragActive] = useState(false);
  const [batch, setBatch] = useState<{current: number; total: number} | null>(null);

  useEffect(() => {
    return () => uploadStore.dispose();
  }, [uploadStore]);

  const handleFiles = useCallback(async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    const arr = Array.from(files);
    if (props.multiple) {
      // Pre-upload filtering keeps us from wasting bandwidth on dupes/overflow.
      let queue = arr;
      if (props.excludeNames && props.excludeNames.size > 0) {
        const before = queue.length;
        queue = queue.filter(f => !props.excludeNames!.has(f.name));
        const skipped = before - queue.length;
        if (skipped > 0) {
          toast.info(`${skipped} datotek je že naloženih.`);
        }
      }
      if (props.maxFiles != null && queue.length > props.maxFiles) {
        toast.info(`Naloženih bo prvih ${props.maxFiles} datotek.`);
        queue = queue.slice(0, props.maxFiles);
      }
      if (queue.length === 0) return;

      const uploaded: uploader.IUFile[] = [];
      try {
        for (let i = 0; i < queue.length; i++) {
          setBatch({current: i + 1, total: queue.length});
          const result = await uploadStore.uploadFile([queue[i]]);
          if (result) uploaded.push(result);
        }
      } finally {
        setBatch(null);
      }
      if (uploaded.length === 0) return;
      if (props.onFilesChanged) {
        props.onFilesChanged(uploaded);
      } else if (props.onFileChanged) {
        for (const u of uploaded) props.onFileChanged(u);
      }
      return;
    }
    const result = await uploadStore.uploadFile(arr);
    if (result) {
      props.onFileChanged?.(result);
    }
  }, [uploadStore, props]);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragActive(false);
    handleFiles(e.dataTransfer.files);
  }, [handleFiles]);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragActive(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragActive(false);
  }, []);

  const handleClick = useCallback(() => {
    inputRef.current?.click();
  }, []);

  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    handleFiles(e.target.files);
    // Reset input so same file can be selected again
    if (inputRef.current) {
      inputRef.current.value = '';
    }
  }, [handleFiles]);

  const handleDelete = useCallback(() => {
    uploadStore.cancel();
    props.onFileChanged?.(undefined);
  }, [uploadStore, props]);

  // Build accept string from extensions
  const acceptString = props.extensions
    ? props.extensions.map(ext => `.${ext}`).join(',')
    : undefined;

  // If we have a successfully uploaded file
  if (props.file) {
    const fileName = props.file.name || 'Datoteka';
    const fileUrl = props.file.src;

    if (props.compact) {
      return (
        <FileDisplay style={{padding: '4px 8px'}}>
          <MdInsertDriveFile size={16} color={theme.content30} />
          {fileUrl ? (
            <FileName href={fileUrl} target="_blank" rel="noopener noreferrer" style={{marginLeft: '4px', fontSize: '13px'}}>
              {fileName}
            </FileName>
          ) : (
            <FileNameText style={{marginLeft: '4px', fontSize: '13px'}}>{fileName}</FileNameText>
          )}
          <DeleteButton onClick={handleDelete} type="button" style={{padding: '2px'}}>
            <MdClose size={16} />
          </DeleteButton>
        </FileDisplay>
      );
    }

    return (
      <UploadWrapper>
        <FileDisplay>
          <MdInsertDriveFile size={24} color={theme.content30} />
          {fileUrl ? (
            <FileName href={fileUrl} target="_blank" rel="noopener noreferrer">
              {fileName}
            </FileName>
          ) : (
            <FileNameText>{fileName}</FileNameText>
          )}
          <DeleteButton onClick={handleDelete} type="button">
            <MdClose size={20} />
          </DeleteButton>
        </FileDisplay>
      </UploadWrapper>
    );
  }

  if (batch) {
    const fileName = uploadStore.file?.name || 'Datoteka';
    const label = `Nalaganje ${batch.current}/${batch.total}: ${fileName}`;
    if (props.compact) {
      return (
        <FileDisplay style={{padding: '4px 8px'}}>
          <MdInsertDriveFile size={16} color={theme.content30} />
          <FileNameText style={{marginLeft: '4px', fontSize: '13px'}}>{label}</FileNameText>
          <ProgressBar progress={uploadStore.progress || 0} style={{marginTop: 0, marginLeft: '8px', width: '60px'}} />
        </FileDisplay>
      );
    }
    return (
      <UploadWrapper>
        <FileDisplay>
          <MdInsertDriveFile size={24} color={theme.content30} />
          <FileNameText>{label}</FileNameText>
          <ProgressBar progress={uploadStore.progress || 0} />
        </FileDisplay>
      </UploadWrapper>
    );
  }

  // If currently uploading (no error yet)
  if (uploadStore.started && !uploadStore.done && !uploadStore.error) {
    const fileName = uploadStore.file?.name || 'Datoteka';

    if (props.compact) {
      return (
        <FileDisplay style={{padding: '4px 8px'}}>
          <MdInsertDriveFile size={16} color={theme.content30} />
          <FileNameText style={{marginLeft: '4px', fontSize: '13px'}}>{fileName}</FileNameText>
          <ProgressBar progress={uploadStore.progress || 0} style={{marginTop: 0, marginLeft: '8px', width: '60px'}} />
        </FileDisplay>
      );
    }

    return (
      <UploadWrapper>
        <FileDisplay>
          <MdInsertDriveFile size={24} color={theme.content30} />
          <FileNameText>{fileName}</FileNameText>
          <ProgressBar progress={uploadStore.progress || 0} />
        </FileDisplay>
      </UploadWrapper>
    );
  }

  // Empty state or error state - show upload zone
  const hasError = !!uploadStore.error;

  // Compact mode - simple button
  if (props.compact) {
    return (
      <>
        <input
          ref={inputRef}
          type="file"
          accept={acceptString}
          multiple={props.multiple}
          onChange={handleInputChange}
          style={{display: 'none'}}
        />
        <CompactUploadButton onClick={handleClick}>
          <MdUploadFile size={16} />
          {hasError ? 'Napaka - poskusi znova' : 'Izberi datoteko'}
        </CompactUploadButton>
      </>
    );
  }

  return (
    <UploadWrapper>
      <input
        ref={inputRef}
        type="file"
        accept={acceptString}
        multiple={props.multiple}
        onChange={handleInputChange}
        style={{display: 'none'}}
      />
      <UploadContainer
        isDragActive={isDragActive}
        hasError={hasError}
        onClick={handleClick}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
      >
        <MdUploadFile size={32} color={hasError ? CvvColors.error : theme.content30} />
        {hasError ? (
          <ErrorText>Napaka pri nalaganju. Poskusite znova.</ErrorText>
        ) : (
          <UploadText>
            {props.label || 'Kliknite ali povlecite datoteko'}
          </UploadText>
        )}
        {props.extensions && (
          <UploadText style={{fontSize: '12px'}}>
            ({props.extensions.map(e => e.toUpperCase()).join(', ')})
          </UploadText>
        )}
      </UploadContainer>
    </UploadWrapper>
  );
});
